import type { OfferRewardDto, OfferRewardType } from "../../../api/dto/offers.dto.js";
import { Vector2 } from "../../../common/vector2.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { LocalizationManager } from "../../../localization/localization_manager.js";
import { BasicEventsController } from "../../../touch_events_controller/basic_events_controller.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement, UITextGradient, UITextOptions } from "../../common_ui_elements/ui_text_element.js";
import { ScreenManager, Screens } from "../../../screen_manager/screen_manager.js";
import { Game } from "../../../game.js";
import { SafeAreas } from "../../safe_areas.js";

interface IOfferRewardLayout
{
  bg_h:        number;
  substrate_w: number;
  substrate_h: number;
  substrate_y: number;
  reward_size: number;
  reward_y:    number;
  reward_cols: number;
  row_gap:     number;
  buy_btn_y:   number;
  shift_up:    number;
}

interface ITitleLayerConfig
{
  outline_width_design: number;
  outline_offset_y_design: number;
  outline_color?: string;
  outline_gradient_color?: UITextGradient;
  shadow_blur_design?: number;
  shadow_color?: string;
  shadow_offset_y_design?: number;
}

export class StartOfferScreen extends UIElement
{
  private readonly BG_W_BASE = 1208;
  private readonly BG_W_HEIGHT_DELTA = 1188 - 1208;
  private readonly BG_W_WIDTH_DELTA = 1463 - 1208;

  private readonly BG_H_BASE = 1796;
  private readonly BG_H_HEIGHT_DELTA = 1766 - 1796;
  private readonly BG_H_WIDTH_DELTA = 2175 - 1796;

  private readonly BG_Y_BASE = -95;
  private readonly BG_Y_HEIGHT_DELTA = -80 - ( -95 );
  private readonly BG_Y_WIDTH_DELTA = -167 - ( -95 );

  private readonly MAIN_IMG_W = 1040;
  private readonly MAIN_IMG_W_HEIGHT_DELTA = 832 - 1040;
  private readonly MAIN_IMG_H = 850;
  private readonly MAIN_IMG_H_HEIGHT_DELTA = 680 - 850;
  private readonly MAIN_IMG_Y_MIN = 350;
  private readonly MAIN_IMG_Y_MAX = 778;

  private readonly TITLE_Y_MIN = 190;
  private readonly TITLE_Y_MAX = 290;
  private readonly TITLE_H = 142;
  private readonly TITLE_W_RATIO = 0.8;
  private readonly TITLE_FONT_SIZE = 100;

  private readonly TITLE_TEXT = "Стартовий набір";

  private readonly OFFER_BG_W = 1018;
  private readonly OFFER_BG_Y_MIN = 1111;
  private readonly OFFER_BG_Y_MAX = 1516;

  private readonly OFFER_REWARD_COLS    = 4;
  private readonly OFFER_REWARD_SIZE    = 186;
  private readonly OFFER_REWARD_Y       = 20;
  private readonly OFFER_REWARD_ROW_GAP = 20;

  private readonly OFFER_SUBSTRATE_W      = 897;
  private readonly OFFER_SUBSTRATE_Y      = 87;
  private readonly OFFER_SUBSTRATE_H_1ROW = 254;
  private readonly OFFER_BG_H_1ROW        = 628;
  private readonly OFFER_BUY_BTN_GAP      = 7;
  private readonly OFFER_BG_BOTTOM_MARGIN = 30;

  private readonly OFFER_REWARDS: ReadonlyArray<OfferRewardDto> = [
    { type: "coins",                   amount: 300 },
    { type: "multi_balls",             amount: 3 },
    { type: "fireworks",               amount: 3 },
    { type: "big_balls",               amount: 3 },
    { type: "infinite_balls",          amount: 3 },
     { type: "hearts",                  amount: 3 },
    // { type: "infinite_hearts_minutes", amount: 60 },
    // { type: "no_ads",                  amount: 1 },
  ];

  private readonly OFFER_REWARD_GAP = 20;
  private readonly OFFER_REWARD_FONT_SIZE = 38;
  private readonly OFFER_REWARD_TEXT_OUTLINE_W = 14;
  private readonly OFFER_REWARD_TEXT_OUTLINE_COLOR = "#103A92";

  private readonly OFFER_BUY_BTN_W = 622;
  private readonly OFFER_BUY_BTN_H = 250;
  private readonly OFFER_BUY_BTN_FONT_SIZE = 80;

  private readonly OFFER_PRICE = "325.99 грн";

  private readonly OFFER_DISCLAIMER_GAP_BELOW_BG = 23;
  private readonly OFFER_DISCLAIMER_H = 60;
  private readonly OFFER_DISCLAIMER_FONT_SIZE = 40;
  private readonly OFFER_DISCLAIMER_OUTLINE_W = 8;
  private readonly OFFER_DISCLAIMER_OUTLINE_COLOR = "#0A85EE";
  private readonly OFFER_DISCLAIMER_TEXT = "Ви можете купити тільки один раз";

  private readonly OFFER_TIMER_SOURCE_W = 288;
  private readonly OFFER_TIMER_SOURCE_H = 78;
  private readonly OFFER_TIMER_H = 92;
  private readonly OFFER_TIMER_Y = -40;
  private readonly OFFER_TIMER_FONT_SIZE = 34;
  private readonly OFFER_TIMER_ICON_AREA_W = 85;
  private readonly OFFER_TIMER_TEXT_GAP = 12;

  private readonly OFFER_TIMER_TEXT = "23:59:59";

  private readonly CLOSE_ICON_SIZE = 110;
  private readonly CLOSE_ICON_X_MIN = 896;
  private readonly CLOSE_ICON_X_MAX = 1064;
  private readonly CLOSE_ICON_Y_MIN = 80;
  private readonly CLOSE_ICON_Y_MAX = 180;

  private events_controller: BasicEventsController | null = null;

  constructor( ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number )
  {
    super( ctx, x, y, width, height, false, false );

    this.initializeEventsController( this.ctx.canvas );

    this.createBackground();
    this.createMainImage();
    this.createTitle();
    this.createOfferBg();
    this.createCloseButton();
  }

  private createBackground(): void
  {
    const bg_w = LayoutScaler.widthFromDesign( this.BG_W_BASE )
      + LayoutScaler.heightFromLayouts( 0, this.BG_W_HEIGHT_DELTA )
      + LayoutScaler.widthFromLayouts( 0, this.BG_W_WIDTH_DELTA );

    const bg_h = LayoutScaler.heightFromDesign( this.BG_H_BASE )
      + LayoutScaler.heightFromLayouts( 0, this.BG_H_HEIGHT_DELTA )
      + LayoutScaler.widthFromLayouts( 0, this.BG_H_WIDTH_DELTA );

    const bg_x = Math.round( ( Game.screenW() - bg_w ) / 2 - Game.safeAreaLeft() );

    const bg_y = LayoutScaler.yFromDesign( this.BG_Y_BASE )
      + LayoutScaler.yFromLayouts( 0, this.BG_Y_HEIGHT_DELTA )
      + LayoutScaler.xFromLayouts( 0, this.BG_Y_WIDTH_DELTA )
      - Game.safeAreaTop();

    const half_w = Math.ceil( bg_w / 2 );
    const right_w = bg_w - half_w;

    const OVERLAP = 2;

    const bg_left = new UIImage( this.ctx, bg_x, bg_y, half_w + OVERLAP, bg_h, ImgID.UI_StartOfferBg );
    bg_left.setFlipX( true );
    bg_left.setClickable( false );
    this.addChild( bg_left );

    const bg_right = new UIImage( this.ctx, bg_x + half_w - OVERLAP, bg_y, right_w + OVERLAP, bg_h, ImgID.UI_StartOfferBg );
    bg_right.setClickable( false );
    this.addChild( bg_right );
  }

  private createMainImage(): void
  {
    const shift_up = LayoutScaler.yFromDesign( this.rewardLayout().shift_up );

    const w = LayoutScaler.widthFromDesign( this.MAIN_IMG_W ) + LayoutScaler.heightFromLayouts( this.MAIN_IMG_W_HEIGHT_DELTA, 0 );
    const h = LayoutScaler.heightFromDesign( this.MAIN_IMG_H ) + LayoutScaler.heightFromLayouts( this.MAIN_IMG_H_HEIGHT_DELTA, 0 );
    const x = ( this.rect.getW() - w ) / 2;
    const y = LayoutScaler.yFromLayouts( this.MAIN_IMG_Y_MIN, this.MAIN_IMG_Y_MAX ) - shift_up
      - LayoutScaler.heightFromLayouts( this.MAIN_IMG_H_HEIGHT_DELTA, 0 );

    const main_img = new UIImage( this.ctx, x, y, w, h, ImgID.UI_StartOfferMainImg );
    main_img.setClickable( false );
    this.addChild( main_img );
  }

  private createTitle(): void
  {
    const title_gradient: UITextGradient = {
      direction: "vertical",
      stops: [
        { offset: 0, color: "#FEF3BB" },
        { offset: 1, color: "#DFB403" },
      ],
    };

    const title_layers: ITitleLayerConfig[] = [
      {
        outline_width_design: 48,
        outline_offset_y_design: 11,
        outline_gradient_color: {
          direction: "vertical",
          stops: [
            { offset: 0, color: "#6C1F03" },
            { offset: 1, color: "#6C1F03" },
          ],
        },
      },
      {
        outline_width_design: 42,
        outline_offset_y_design: 7,
        outline_gradient_color: {
          direction: "vertical",
          stops: [
            { offset: 0, color: "#F8AE26" },
            { offset: 1, color: "#F8AE26" },
          ],
        },
      },
      {
        outline_width_design: 29,
        outline_offset_y_design: 7,
        outline_gradient_color: {
          direction: "vertical",
          stops: [
            { offset: 0, color: "#042385" },
            { offset: 1, color: "#032080" },
          ],
        },
      },
      {
        outline_width_design: 25,
        outline_offset_y_design: 3,
        outline_color: "#0062FB",
        shadow_blur_design: 35,
        shadow_color: "rgba(100, 27, 0, 0.89)",
        shadow_offset_y_design: 5,
      },
    ];

    const title_w = this.rect.getW() * this.TITLE_W_RATIO;
    const title_x = ( this.rect.getW() - title_w ) / 2;
    const title_y = LayoutScaler.yFromLayouts( this.TITLE_Y_MIN, this.TITLE_Y_MAX );
    const title_h = LayoutScaler.heightFromDesign( this.TITLE_H );

    for ( const layer of title_layers )
    {
      const title = new UITextElement( this.ctx, title_x, title_y, title_w, title_h, {
        text: this.TITLE_TEXT,
        font_size: LayoutScaler.textSizeFromDesign( this.TITLE_FONT_SIZE ),
        font_weight: 900,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        scale_to_width: true,
        text_color: "#DFB403",
        gradient_text_color: title_gradient,
        outline_width: LayoutScaler.textBorderSizeFromDesign( layer.outline_width_design ),
        outline_color: layer.outline_color,
        outline_gradient_color: layer.outline_gradient_color,
        outline_offset_y: LayoutScaler.yFromDesign( layer.outline_offset_y_design ),
        shadow_blur: layer.shadow_blur_design !== undefined ? LayoutScaler.textBorderSizeFromDesign( layer.shadow_blur_design ) : undefined,
        shadow_color: layer.shadow_color,
        shadow_offset_y: layer.shadow_offset_y_design !== undefined ? LayoutScaler.yFromDesign( layer.shadow_offset_y_design ) : undefined,
      } );
      this.addChild( title );
    }
  }

  private rewardLayout(): IOfferRewardLayout
  {
    const cols = this.OFFER_REWARD_COLS;
    const rows = Math.max( 1, Math.ceil( this.OFFER_REWARDS.length / cols ) );

    const substrate_h = this.OFFER_SUBSTRATE_H_1ROW
      + ( rows - 1 ) * ( this.OFFER_REWARD_SIZE + this.OFFER_REWARD_ROW_GAP );

    const buy_btn_y = this.OFFER_SUBSTRATE_Y + substrate_h + this.OFFER_BUY_BTN_GAP;
    const bg_h = buy_btn_y + this.OFFER_BUY_BTN_H + this.OFFER_BG_BOTTOM_MARGIN;
    const shift_up = Math.max( 0, bg_h - this.OFFER_BG_H_1ROW );

    return {
      bg_h,
      substrate_w: this.OFFER_SUBSTRATE_W,
      substrate_h,
      substrate_y: this.OFFER_SUBSTRATE_Y,
      reward_size: this.OFFER_REWARD_SIZE,
      reward_y:    this.OFFER_REWARD_Y,
      reward_cols: cols,
      row_gap:     this.OFFER_REWARD_ROW_GAP,
      buy_btn_y,
      shift_up,
    };
  }

  private createOfferBg(): void
  {
    const layout = this.rewardLayout();

    const bg_w = LayoutScaler.widthFromDesign( this.OFFER_BG_W );
    const bg_h = LayoutScaler.heightFromDesign( layout.bg_h );
    const bg_x = ( this.rect.getW() - bg_w ) / 2;
    const bg_y = LayoutScaler.yFromLayouts( this.OFFER_BG_Y_MIN, this.OFFER_BG_Y_MAX ) - LayoutScaler.yFromDesign( layout.shift_up );

    const bg = new UINineSliceElement(
      this.ctx,
      bg_x,
      bg_y,
      bg_w,
      bg_h,
      ImgID.UI_CapsuleBlue,
      "capsule_bg"
    );
    this.addChild( bg );

    this.createOfferTimer( bg, bg_w );
    this.createOfferSubstrate( bg, bg_w, layout );
    this.createOfferBuyButton( bg, bg_w, layout );
    this.createOfferDisclaimer( bg, bg_w, bg_h );
  }

  private createOfferTimer( bg: UINineSliceElement, bg_w: number ): void
  {
    const timer_h = LayoutScaler.heightFromDesign( this.OFFER_TIMER_H );
    const timer_w = Math.round( timer_h * this.OFFER_TIMER_SOURCE_W / this.OFFER_TIMER_SOURCE_H );
    const timer_x = ( bg_w - timer_w ) / 2;
    const timer_y = LayoutScaler.yFromDesign( this.OFFER_TIMER_Y );

    const timer_bg = new UIImage( this.ctx, timer_x, timer_y, timer_w, timer_h, ImgID.UI_NoAdsTimer );
    bg.addChild( timer_bg );

    const text_gap = LayoutScaler.xFromDesign( this.OFFER_TIMER_TEXT_GAP );
    const text_x   = LayoutScaler.widthFromDesign( this.OFFER_TIMER_ICON_AREA_W );
    const text_w   = timer_w - text_x - text_gap;

    const timer_text = new UITextElement( this.ctx, text_x, 0, text_w, timer_h, {
      text:           this.OFFER_TIMER_TEXT,
      font_size:      LayoutScaler.textSizeFromDesign( this.OFFER_TIMER_FONT_SIZE ),
      font_weight:    900,
      text_style:     TextStyleScheme.geologica,
      text_align:     MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      text_color:     ColorScheme.white,
      scale_to_width: true,
      auto_wrap:      false,
    } );
    timer_bg.addChild( timer_text, true );
  }

  private createOfferBuyButton( bg: UINineSliceElement, bg_w: number, layout: IOfferRewardLayout ): void
  {
    const btn_w = LayoutScaler.widthFromDesign( this.OFFER_BUY_BTN_W );
    const btn_h = LayoutScaler.heightFromDesign( this.OFFER_BUY_BTN_H );
    const btn_x = ( bg_w - btn_w ) / 2;
    const btn_y = LayoutScaler.yFromDesign( layout.buy_btn_y );

    const btn = new UINineSliceElement(
      this.ctx,
      btn_x,
      btn_y,
      btn_w,
      btn_h,
      ImgID.UI_GreenFrameBtn,
      "frame_btn",
      undefined,
      undefined,
      true
    );
    bg.addChild( btn );

    const price_text = new UITextElement(
      this.ctx, 0, 0, btn_w, btn_h,
      {
        text:           this.OFFER_PRICE,
        font_size:      LayoutScaler.textSizeFromDesign( this.OFFER_BUY_BTN_FONT_SIZE ),
        font_weight:    800,
        text_color:     ColorScheme.white,
        text_style:     TextStyleScheme.geologica,
        text_align:     MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap:      false,
        scale_to_width: true,
        ...TextStyleScheme.buttonShadow(),
      }
    );
    btn.addChild( price_text, true );
  }

  private createOfferDisclaimer( bg: UINineSliceElement, bg_w: number, bg_h: number ): void
  {
    const y = bg_h + LayoutScaler.yFromDesign( this.OFFER_DISCLAIMER_GAP_BELOW_BG );
    const h = LayoutScaler.heightFromDesign( this.OFFER_DISCLAIMER_H );

    const text = new UITextElement( this.ctx, 0, y, bg_w, h, {
      text:           this.OFFER_DISCLAIMER_TEXT,
      font_size:      LayoutScaler.textSizeFromDesign( this.OFFER_DISCLAIMER_FONT_SIZE ),
      font_weight:    800,
      text_style:     TextStyleScheme.geologica,
      text_align:     MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      text_color:     ColorScheme.white,
      outline_width:  LayoutScaler.textBorderSizeFromDesign( this.OFFER_DISCLAIMER_OUTLINE_W ),
      outline_color:  this.OFFER_DISCLAIMER_OUTLINE_COLOR,
      auto_wrap:      false,
      scale_to_width: true,
    } );
    bg.addChild( text );
  }

  private createOfferSubstrate( bg: UINineSliceElement, bg_w: number, layout: IOfferRewardLayout ): void
  {
    const substrate_w = LayoutScaler.widthFromDesign( layout.substrate_w );
    const substrate_h = LayoutScaler.heightFromDesign( layout.substrate_h );
    const substrate_x = ( bg_w - substrate_w ) / 2;
    const substrate_y = LayoutScaler.yFromDesign( layout.substrate_y );

    const substrate = new UINineSliceElement(
      this.ctx,
      substrate_x,
      substrate_y,
      substrate_w,
      substrate_h,
      ImgID.UI_TowerFailDarkBlueSubstrate,
      "tower_fail_dark_blue_substrate"
    );
    bg.addChild( substrate );

    this.createOfferRewards( substrate, substrate_w, layout );
  }

  private rewardIcon( type: OfferRewardType ): ImgID
  {
    switch ( type )
    {
      case "coins":                   return ImgID.IconCoin;
      case "hearts":                  return ImgID.IconHeart;
      case "big_balls":               return ImgID.UI_GameStartBigBallBooster;
      case "infinite_balls":          return ImgID.UI_GameStartInfiniteBallsBooster;
      case "multi_balls":             return ImgID.UI_GameStartMultiballsBooster;
      case "fireworks":               return ImgID.UI_RocketBooster;
      case "mines":                   return ImgID.UI_PassiveBoosterBomb;
      case "infinite_hearts_minutes": return ImgID.UI_ShopInfiniteHeart;
      case "no_ads": case "no_ads_minutes": return ImgID.UI_RemoveAdsIcon;
      case "level_shots":             return ImgID.UI_DailyTasksTaskCannon;
    }
  }

  private formatRewardAmount( reward: OfferRewardDto ): string
  {
    if ( reward.type === "coins" )
      return `${reward.amount}`;

    if ( reward.type === "infinite_hearts_minutes" )
    {
      const hours = Math.floor( reward.amount / 60 );
      const minutes = reward.amount % 60;
      const h = LocalizationManager.t( "TIME.SHORT_HOURS" );
      const m = LocalizationManager.t( "TIME.SHORT_MINUTES" );
      return hours && minutes ? `${hours}${h} ${minutes}${m}` : hours ? `${hours}${h}` : `${minutes}${m}`;
    }

    return `x${reward.amount}`;
  }

  private offerRewardTextOptions(): UITextOptions
  {
    return {
      font_size:      LayoutScaler.textSizeFromDesign( this.OFFER_REWARD_FONT_SIZE ),
      font_weight:    900,
      text_color:     ColorScheme.white,
      text_style:     TextStyleScheme.geologica,
      text_align:     MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      outline_width:  LayoutScaler.textBorderSizeFromDesign( this.OFFER_REWARD_TEXT_OUTLINE_W ),
      outline_color:  this.OFFER_REWARD_TEXT_OUTLINE_COLOR,
      auto_wrap:      false,
      scale_to_width: true,
    };
  }

  private createOfferRewards( substrate: UINineSliceElement, substrate_w: number, layout: IOfferRewardLayout ): void
  {
    const design_text_h        = 60;
    const design_text_slop     = 20;
    const design_text_offset_y = 10;

    const icon_w = LayoutScaler.widthFromDesign( layout.reward_size );
    const icon_h = LayoutScaler.heightFromDesign( layout.reward_size );
    const icon_y0 = LayoutScaler.yFromDesign( layout.reward_y );
    const gap     = LayoutScaler.widthFromDesign( this.OFFER_REWARD_GAP );
    const row_gap = LayoutScaler.heightFromDesign( layout.row_gap );

    const text_slop = LayoutScaler.widthFromDesign( design_text_slop );
    const text_h    = LayoutScaler.heightFromDesign( design_text_h );

    const cols  = layout.reward_cols;
    const total = this.OFFER_REWARDS.length;

    this.OFFER_REWARDS.forEach( ( reward, i ) =>
    {
      const row = Math.floor( i / cols );
      const col = i % cols;

      const items_in_row = Math.min( cols, total - row * cols );
      const row_w         = icon_w * items_in_row + gap * ( items_in_row - 1 );
      const row_start_x   = ( substrate_w - row_w ) / 2;

      const icon_x = row_start_x + col * ( icon_w + gap );
      const icon_y = icon_y0 + row * ( icon_h + row_gap );

      const icon = new UIImage( this.ctx, icon_x, icon_y, icon_w, icon_h, this.rewardIcon( reward.type ) );
      substrate.addChild( icon );

      icon.addChild( new UITextElement(
        this.ctx,
        -text_slop,
        icon_h - text_h + LayoutScaler.yFromDesign( design_text_offset_y ),
        icon_w + text_slop * 2,
        text_h,
        {
          text: this.formatRewardAmount( reward ),
          ...this.offerRewardTextOptions(),
        }
      ), true );
    } );
  }

  private createCloseButton(): void
  {
    const close_icon_w = LayoutScaler.widthFromDesign( this.CLOSE_ICON_SIZE );
    const close_icon_h = LayoutScaler.heightFromDesign( this.CLOSE_ICON_SIZE );
    const close_icon_x = LayoutScaler.xFromLayouts( this.CLOSE_ICON_X_MIN, this.CLOSE_ICON_X_MAX );
    const close_icon_y = LayoutScaler.yFromLayouts( this.CLOSE_ICON_Y_MIN, this.CLOSE_ICON_Y_MAX );

    const close_icon = new UIImage( this.ctx, close_icon_x, close_icon_y, close_icon_w, close_icon_h, ImgID.UI_SettingsDialogCloseBtn, {}, true );
    this.addChild( close_icon );

    close_icon.setOnClick( () => ScreenManager.showScreen( Screens.MENU ) );
  }

  private initializeEventsController( canvas: HTMLCanvasElement ): void
  {
    if ( this.events_controller )
      this.events_controller.cleanup();

    this.events_controller = new BasicEventsController( canvas );
    this.events_controller.init(
      ( p: Vector2 ) => { super.onTouchStart( p, performance.now() ); },
      ( p: Vector2 ) => { super.onTouchEnd( p, performance.now() ); },
      ( p: Vector2 ) => { super.onTouchMove( p, performance.now() ); },
      true
    );
  }

  public destroy(): void
  {
    if ( this.events_controller )
    {
      this.events_controller.cleanup();
      this.events_controller = null;
    }
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.ctx.fillStyle = "#04409C";
    this.ctx.fillRect( 0, 0, Game.screenW(), Game.screenH() );

    this.startRender();
    this.finishRender();
    SafeAreas.renderBothSafeAreaOverlays( this.ctx );
  }

  public update( now: number ): void
  {
    this.updateBasic( now );
  }

  public name(): string
  {
    return "StartOfferScreen";
  }
}
